import { track } from '@eventbrite/datalayer-library';
import { Button, SIZE_BLOCK, STYLE_FILL } from '@eventbrite/eds-button';
import { gettext } from '@eventbrite/i18n';
import ButtonFromGoogle from '@google-pay/button-react';
import { GooglePayment } from 'braintree-web';
import React, { useEffect } from 'react';
import { HeapEventName } from '../../../constants/heap';
import type { ExpressCheckoutSubmitDataType } from '../../../models/expressCheckout';
import { StatsigValueType } from '../../../models/expressCheckout';
import './GooglePayButton.scss';

export interface GooglePayButtonProps {
    clearError: () => void;
    defaultLocale: string;
    environment: 'TEST' | 'PRODUCTION';
    handleOnCancel: () => void;
    handleOnClick: (isAndroidApp: boolean) => void;
    handleOnError: (e: google.payments.api.PaymentsError | Error) => void;
    handleOnLoadPaymentData: (
        paymentData: google.payments.api.PaymentData,
        instance: GooglePayment,
    ) => void;
    handleSubmit?: (props: ExpressCheckoutSubmitDataType) => void;
    instance: GooglePayment;
    isAndroidApp: boolean;
    isDisabled?: boolean;
    paymentRequest?: google.payments.api.PaymentDataRequest;
    buttonColor?: google.payments.api.ButtonColor;
    pathname: string;
}

export const GooglePayButton: React.FunctionComponent<GooglePayButtonProps> = ({
    clearError,
    defaultLocale,
    environment,
    handleOnCancel,
    handleOnClick,
    handleOnError,
    handleOnLoadPaymentData,
    handleSubmit,
    instance,
    isAndroidApp,
    isDisabled,
    paymentRequest,
    buttonColor = 'default',
    pathname,
}) => {
    useEffect(() => {
        if (isAndroidApp) {
            const googlePayButtonInAndroidApp =
                new google.payments.api.PaymentsClient({
                    environment,
                }).createButton({
                    buttonColor,
                    buttonType: 'buy',
                    onClick: () => handleOnClick(true),
                });

            const buttonContainer = document.getElementById(
                'google-pay-button-wrapper',
            );

            googlePayButtonInAndroidApp.classList.add('google-pay-button');

            buttonContainer?.children?.[0]?.replaceWith(
                googlePayButtonInAndroidApp,
            );
        }

        return () => clearError();
    }, [clearError, environment, handleOnClick, isAndroidApp, buttonColor]);

    const onGooglePayClickHandler = (event: Event) => {
        if (handleSubmit) {
            handleSubmit({
                isGooglePay: true,
                statsigValue: StatsigValueType.GOOGLE_PAY,
            });
        }

        if (isDisabled) return event.preventDefault();

        handleOnClick(false);
    };

    useEffect(() => {
        track({
            eventName: HeapEventName.googlePayButtonView,
            eventData: {
                pathname,
            },
        });
    }, [pathname]);

    const placeholderButton = (
        <div className="placeholder-button-wrapper">
            <Button
                data-testid="google-pay-button-placeholder"
                disabled={true}
                isLoading={true}
                size={SIZE_BLOCK}
                style={STYLE_FILL}
            >
                {gettext('Place Order')}
            </Button>
        </div>
    );

    const googlePayButtonInWebApp =
        paymentRequest && !isAndroidApp ? (
            <ButtonFromGoogle
                buttonLocale={defaultLocale}
                buttonType="buy"
                buttonColor={buttonColor}
                className="google-pay-button"
                environment={environment}
                onCancel={handleOnCancel}
                onClick={onGooglePayClickHandler}
                onError={(e) => {
                    handleOnError(e);
                }}
                onLoadPaymentData={(paymentData) => {
                    handleOnLoadPaymentData(paymentData, instance);
                }}
                paymentRequest={paymentRequest}
            />
        ) : (
            placeholderButton
        );

    return (
        <div
            className="google-pay-button-wrapper"
            id="google-pay-button-wrapper"
        >
            {googlePayButtonInWebApp}
        </div>
    );
};
